import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getFeedback, postFeedback } from '../../DashBoard/Api';
import Loaders from '../../../../GlobalComponents/KenLoader';
import { FeedBackData } from '../../Interfaces/IDashBoard';
import Fields from './Fields';
import { makeStyles } from '@mui/styles';
import { Typography } from '@mui/material';
import storageService from '../../../../Utils/storageService';

/** Merge template `feedback` with saved answers from `feedbackScore.response` (same shape, keyed by section/question). */
const mergeFeedbackWithSavedResponse = (template: any[], saved: any[] | undefined | null): any[] => {
  if (!Array.isArray(template) || template.length === 0) return template;
  if (!Array.isArray(saved) || saved.length === 0) return template;

  return template.map((section) => {
    const savedSection = saved.find(
      (s: any) => s.sectionId === section.sectionId || s.sectionLabel === section.sectionLabel,
    );
    if (!savedSection?.parameters?.length) return section;

    const mergedParameters = section.parameters.map((param: any, idx: number) => {
      const byId = savedSection.parameters.find((p: any) => p.questionId === param.questionId);
      const savedParam = byId ?? savedSection.parameters[idx];
      if (!savedParam) return param;

      const next = { ...param };
      if (savedParam.questionResponse !== null && savedParam.questionResponse !== undefined && savedParam.questionResponse !== '') {
        next.questionResponse = savedParam.questionResponse;
      }
      if (savedParam.comments !== null && savedParam.comments !== undefined) {
        next.comments = savedParam.comments;
      }
      if (savedParam.weightedScore !== undefined) next.weightedScore = savedParam.weightedScore;
      if (savedParam.avgScore !== undefined) next.avgScore = savedParam.avgScore;
      return next;
    });

    return { ...section, parameters: mergedParameters };
  });
};

export interface IStartupDetailProps {
  startUpDetails: string | undefined;
  ref: any;
  setAvgScore: any;
  avgScore: any;
  // Sourced from the parent's already-correctly-keyed data fetch — passed as
  // props (synchronous, always consistent with the current render) instead of
  // this component independently re-reading the same values from async
  // storage, which raced against the parent's own writes to that storage and
  // could resolve to a previous startup's identifiers.
  applicationId?: string;
  contactId?: string;
  round?: string;
  // Lifted to the parent (same pattern as avgScore) so Startup/index.tsx can
  // render the Save/Submit buttons itself, in its own tab-header row - same
  // spot the other tabs' header content (like the Average score badge) lives,
  // instead of a separate floating/bottom bar local to this component.
  evaluted?: boolean;
  setEvaluted?: any;
  isSubmitting?: boolean;
  setIsSubmitting?: any;
}

const useStyles = makeStyles(() => ({
  feedBackScrollContainer: {
    // Was a fixed 320px (380px only above 1920px) regardless of the actual
    // viewport - the scoring form got squeezed into a small scrollable box even
    // on a tall screen with plenty of room. This app is Ionic-based (IonApp),
    // whose core CSS sets html/body overflow:hidden by design (content is
    // expected to scroll via IonContent, which this page doesn't use) - so
    // this box's own explicit overflow:auto is the ONLY way anything on this
    // page can scroll at all; there's no outer page scroll to fall back on.
    minHeight: '320px',
    maxHeight: '65vh',
    overflow: 'auto',
    // The floating Notes button (position:fixed, portaled to document.body -
    // see Startup/index.tsx) sits at a fixed viewport position independent of
    // this box's own scroll, so the last card needs enough bottom padding to
    // clear it instead of scrolling to a stop right underneath it.
    padding: '10px 20px 110px 20px',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 600px)': {
      padding: '6px 6px 110px 6px',
      maxHeight: '55vh',
    },
  },
}));

const FeedBack: React.FunctionComponent<IStartupDetailProps> = forwardRef((props, ref) => {
  const classes = useStyles();
  const {
    avgScore,
    setAvgScore,
    applicationId: applicationIdProp,
    contactId: contactIdProp,
    round: roundProp,
    evaluted: evaluatedProp,
    setEvaluted: setEvaluatedProp,
    isSubmitting: isSubmittingProp,
    setIsSubmitting: setIsSubmittingProp,
  } = props;
  const { id } = useParams<{ id: string }>(); // Get application ID from URL
  const location: any = useLocation();
  const isViewMode = Boolean(location?.state?.isEvaluated);

  const [teamStrength, setTeamStrength] = useState<any>({});
  const [feedback, setfeedback] = useState<any>([]);
  const [data, setData] = useState<any>({});
  const [select, setSelect] = useState<any>();
  const [impPayload, setImpPayload] = useState<any>();
  const { enqueueSnackbar } = useSnackbar();
  // Falls back to local state when the parent doesn't pass these (defensive -
  // in practice Startup/index.tsx always does, so it can render the
  // Save/Submit buttons itself in its tab-header row).
  const [localEvaluted, setLocalEvaluted] = useState(false);
  const evaluted = evaluatedProp !== undefined ? evaluatedProp : localEvaluted;
  const setEvaluated = setEvaluatedProp || setLocalEvaluted;
  const [recordId, setRecordId] = useState(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [localIsSubmitting, setLocalIsSubmitting] = useState<boolean>(false);
  const isSubmitting = isSubmittingProp !== undefined ? isSubmittingProp : localIsSubmitting;
  const setIsSubmitting = setIsSubmittingProp || setLocalIsSubmitting;
  const [feedbackScore, setFeedbackScore] = useState<any>();

  const theme = useTheme();
  const isMobilescreen = useMediaQuery('(max-width:600px)');

  const [contactId, setContactId] = useState<string>('');
  const [applicationId, setApplicationId] = useState<string>('');
  const [round, setRound] = useState<string>('');
  const [panelId, setPanelId] = useState<string>('');
  const [savedScoreId, setSavedScoreId] = useState<string | null>(null);

  // Prefer the parent's props — synchronous, always consistent with the
  // current startup being viewed. Only fall back to the async storage read
  // (which can race against the parent's own writes to that same storage,
  // and previously resolved to a *different* startup's identifiers) when
  // the props genuinely aren't available.
  useEffect(() => {
    if (applicationIdProp && contactIdProp) {
      setContactId(contactIdProp);
      setApplicationId(applicationIdProp);
      setRound(roundProp || '');
      storageService.getItem('PanelId').then((panelIdData) => setPanelId(panelIdData || ''));
      storageService.getItem('evaluationScoreId').then((storedScoreId) => {
        if (storedScoreId) setSavedScoreId(storedScoreId);
      });
      setfeedback([]);
      setData({});
      setEvaluated(false);
      return;
    }

    const loadStorageData = async () => {
      try {
        const contactIdData = await storageService.getItem('contactId');
        const applicationIdData = await storageService.getItem('applicationId');
        const roundData = await storageService.getItem('appStatus');
        const panelIdData = await storageService.getItem('PanelId');
        const userData = await storageService.getItem('user');
        const storedScoreId = await storageService.getItem('evaluationScoreId');

        // If contactId is not in storage, try to get it from user data
        const finalContactId = contactIdData || userData?.accountId || '';
        const finalApplicationId = applicationIdData || id || ''; // Use URL param as fallback

        setContactId(finalContactId);
        setApplicationId(finalApplicationId);
        setRound(roundData || '');
        setPanelId(panelIdData || '');
        if (storedScoreId) setSavedScoreId(storedScoreId);
      } catch (error) {
        console.error('Error loading storage data:', error);
      }
    };
    loadStorageData();
    // Reset feedback/evaluation state from the previous startup immediately —
    // otherwise the old startup's data stays visible while this reloads.
    setfeedback([]);
    setData({});
    setEvaluated(false);
    // Re-run whenever the route id OR the parent's props change (navigating
    // between startup profiles reuses the same component instance — this
    // previously ran once ever, so applicationId/contactId/round went stale
    // the moment you navigated to a second startup and back).
  }, [id, applicationIdProp, contactIdProp, roundProp]);

  useEffect(() => {
    // Make sure we have all required parameters before making API call
    if (contactId && applicationId) {
      // Use 'Shortlisted' as default round if not available
      const finalRound = round || 'Shortlisted';

      // Race guard: React StrictMode double-fires this effect in dev, and rapid
      // navigation between startups can leave older requests in flight. Without
      // this, a slower/stale response can resolve after a newer one and silently
      // overwrite the correct startup's data with a previous startup's data.
      let cancelled = false;

      setLoader(true);

      getFeedback(contactId, applicationId, finalRound)
        .then((response) => {
          if (cancelled) return;
          if (response) {
            setLoader(false);

            // Template questions live under `feedback`; saved answers live under `feedbackScore.response`.
            const feedbackTemplate = response?.data?.data?.feedback || [];
            const feedbackScoreObj = response?.data?.data?.feedbackScore;
            const savedSections = feedbackScoreObj?.response;
            const feedbackData = mergeFeedbackWithSavedResponse(feedbackTemplate, savedSections);
            const attachments = response?.data?.data?.attachments || [];
            const teamMembers = response?.data?.data?.teamMembers || [];
            const application = response?.data?.data?.application || {};
            
            setfeedback(feedbackData);
            setData({
              feedback: feedbackData,
              attachments: attachments,
              teamMembers: teamMembers,
              application: application
            });

            const scoreIdFromGet =
              feedbackScoreObj?.scoreRecordId ||
              response?.data?.data?.scoreRecordId ||
              feedbackData?.find((s: any) => s.scoreRecordId)?.scoreRecordId ||
              null;
            if (scoreIdFromGet) {
              setSavedScoreId(scoreIdFromGet);
              storageService.setItem('evaluationScoreId', scoreIdFromGet);
            }

            // Was also true whenever ANY question had ANY saved score at all -
            // which a draft save does too, so reloading a merely-saved (not
            // submitted) evaluation wrongly showed "Submitted". evaluated on
            // the record itself (set from handleSubmit's own `type !== 'save'`)
            // is the only real signal that it was actually submitted.
            const isEvaluated = feedbackScoreObj?.evaluated === true;

            if (isEvaluated) {
              setEvaluated(true);
            }

            if (feedbackScoreObj?.avgScore !== null && feedbackScoreObj?.avgScore !== undefined) {
              setAvgScore(Number(feedbackScoreObj.avgScore).toFixed(2));
            }
          }
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('API Error:', err);
          enqueueSnackbar('Something went wrong', { variant: 'error' });
          setLoader(false);
        });

      // Runs before the next effect invocation (StrictMode's second pass, a
      // dependency change, or unmount) — marks this run's response as stale
      // so it can no longer write to state once it resolves.
      return () => {
        cancelled = true;
      };
    }
  }, [contactId, applicationId, round]);

  React.useEffect(() => {
    if (feedback) {
      setImpPayload(feedback);
    }
  }, [feedback]);

  const currentScoreId = savedScoreId || feedback[0]?.scoreRecordId || data?.scoreRecordId || null;

  React.useEffect(() => {
    setFeedbackScore(currentScoreId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useImperativeHandle(ref, () => ({
    handleSubmit: (type: string) => {
      handleSubmit(type);
    },
  }));

  const handleSubmit = (type: string) => {
    if (isViewMode) {
      enqueueSnackbar('This evaluation is in view mode and cannot be submitted again.', {
        variant: 'info',
      });
      return;
    }

    if (!impPayload?.length) {
      enqueueSnackbar('No evaluation data available to submit.', {
        variant: 'warning',
      });
      return;
    }

    if (type === 'submit' && evaluted) {
      enqueueSnackbar('Feedback has already been submitted for this application.', {
        variant: 'info',
      });
      return;
    }

    if (isSubmitting) {
      return;
    }

    let allFieldsAreMandatory = true;

    impPayload?.forEach((el: any) => {
      if (!el.parameters || el.parameters.some((elem: any) => elem.weightage !== 0 && (elem.questionResponse === null || elem.questionResponse === undefined))) {
        allFieldsAreMandatory = false;
        return;
      }
    });

    const payload: any = {
      memberId: contactId,
      panelAssociationId: panelId,
      response: impPayload,
      evaluated: type !== 'save',
      avgScore: avgScore,
    };
    if (currentScoreId) {
      payload.scoreRecordId = currentScoreId;
    }

    if (allFieldsAreMandatory && payload.evaluated) {
      // condition for submit button
      setIsSubmitting(true);
      postFeedback(payload)
        .then((res) => {
          if (res) {
            setEvaluated(true);
            storageService.removeItem('evaluationScoreId');
            setSavedScoreId(null);
            enqueueSnackbar('The feedback has been posted for this application', {
              variant: 'success',
            });
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else if (!payload.evaluated) {
      // condition for save button
      setIsSubmitting(true);
      postFeedback(payload)
        .then((res) => {
          if (res) {
            const newScoreId = res.data?.evaluationScoreId || null;
            if (newScoreId) {
              setSavedScoreId(newScoreId);
              storageService.setItem('evaluationScoreId', newScoreId);
            }
            enqueueSnackbar('Evaluation score saved.', {
              variant: 'success',
            });
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      // Handle the case where not all fields are mandatory
      enqueueSnackbar('Please enter feedback for all the parameters', {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    const calculateAverageScore = () => {
      let totalScore = 0;

      // Same fix as Fields.tsx's handleData - weightage is a CATEGORY-level
      // percentage (repeated on every question within that section), so it
      // must be applied once to the category's own average, not multiplied
      // into and summed across every individual question (which over-weighted
      // any category with more than one question). Matches the authoritative
      // calculation Salesforce now performs server-side.
      impPayload?.forEach((el: any) => {
        const validParams = (el.parameters || []).filter(
          (param: any) => param.questionResponse !== undefined && param.questionResponse !== null && param.weightage !== 0 && /\d/.test(param.questionResponse)
        );
        if (validParams.length === 0) return;
        const categoryAverage = validParams.reduce((sum: number, param: any) => sum + Number.parseFloat(param.questionResponse), 0) / validParams.length;
        const categoryWeightage = validParams[0].weightage;
        totalScore += (categoryAverage * categoryWeightage) / 100;
      });

      const averageScore = totalScore;
      setAvgScore(averageScore.toFixed(2));
    };

    // Call the function when the component mounts
    calculateAverageScore();
  }, [impPayload, setAvgScore]);

  return (
    <>
      {loader && <Loaders />}
      <Box 
        component={'div'} 
        className={`w-100 ${classes.feedBackScrollContainer}`}
        sx={{
          minHeight: '300px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid container spacing={2} width={'100%'} sx={{ flex: 1 }}>
          {impPayload?.length > 0 ? (() => {
            // Two independent columns instead of letting each section wrap as its
            // own lg={6} grid item: with sections paired left/right by row order,
            // a row's height was set by whichever side had more questions, leaving
            // a large gap under the shorter side before the next row began.
            // Assignment is greedy-balanced by question count (not just
            // alternating index) - a plain even/odd split could still stack
            // several long sections on one side and several short ones on the
            // other, leaving that side visibly longer than its partner.
            const columnTotals = [0, 0];
            const columnOf: number[] = impPayload.map((section: any) => {
              const weight = section?.parameters?.length || 1;
              const col = columnTotals[0] <= columnTotals[1] ? 0 : 1;
              columnTotals[col] += weight;
              return col;
            });
            return [0, 1].map((columnIndex) => (
              <Grid item lg={6} xs={12} md={12} key={columnIndex}>
                <Box className="d-flex flex-column" sx={{ gap: 2 }}>
                  {impPayload
                    .map((el: any, indexMain: number): { el: any; indexMain: number } => ({ el, indexMain }))
                    .filter(({ indexMain }: { indexMain: number }) => columnOf[indexMain] === columnIndex)
                    .map(({ el, indexMain }: { el: any; indexMain: number }) => (
                      <Box key={indexMain}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#333', fontSize: '16px', fontWeight: '600' }}>
                          {el.sectionLabel || `Section ${indexMain + 1}`}
                        </Typography>
                        {el?.parameters?.map((item: FeedBackData, indexParam: number) => {
                          return (
                            <Box key={`${indexMain}-${indexParam}`} sx={{ mb: 2 }}>
                              <Fields
                                type={item}
                                indexParam={indexParam}
                                indexMain={indexMain}
                                isMobilescreen={isMobilescreen}
                                teamStrength={teamStrength}
                                impPayload={impPayload}
                                setImpPayload={setImpPayload}
                                select={select}
                                evaluted={evaluted || isViewMode}
                                setAvgScore={setAvgScore}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                </Box>
              </Grid>
            ));
          })() : (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body1" sx={{ fontSize: '14px', color: '#8C8E94' }}>
                  No Evaluation Data Available
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#999', mt: 1 }}>
                  {!contactId || !applicationId 
                    ? 'Missing required parameters (contactId or applicationId)' 
                    : 'No feedback data returned from API'
                  }
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      {/* Save/Submit buttons now render in Startup/index.tsx's own tab-header
          row (next to the Average score badge) instead of here - see the
          evaluted/isSubmitting props this component receives from that
          parent, and its handleSubmit exposed via useImperativeHandle above. */}
    </>
  );
});

export default FeedBack;
